import { CSSProperties, useMemo } from 'react';
import {
  intlNumberFormatter,
  IntlNumberFormatterOptions,
} from 'react-formatted-input-hook';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';

type Params = {
  /**
   * Optional options to customize the number formatter.
   * @param minimumFractionDigits - The minimum number of fraction digits to display. Defaults to 0.
   * @param maximumFractionDigits - The maximum number of fraction digits to display. Defaults to 2.
   */
  options?: Omit<
    IntlNumberFormatterOptions,
    'liveUpdate' | 'onChange' | 'value'
  >;
};

/**
 * A hook that returns a number formatter function based on the user's locale and formatting preferences.
 * @param options - Optional options to customize the number formatter.
 * @returns A number formatter function that can be used to format numbers.
 */
export const useNumberFormatter = (params: Params) => {
  const options = params?.options ?? {};

  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  const locale = (
    getProfileField('formattingConfig')?.numbersFormat
      ? getProfileField('formattingConfig')?.numbersFormat
      : i18n.language
  ) as string;

  return useMemo(() => {
    const fmt = intlNumberFormatter({ locales: locale, ...options });

    return {
      format: (value: string | number) =>
        fmt.format?.(fmt.onBlur?.(`${value}`) ?? ''),
      value: fmt.value,
    };
  }, [locale, ...Object.values(options)]);
};

/**
 * A component that displays a formatted numeric value.
 * @param number - The numeric value to display.
 * @param options - Optional options to customize the number formatter.
 * @returns A React component that displays the formatted numeric value.
 */
const StaticNumericSpan = ({
  number,
  options = {},
  sx = {},
}: {
  /**
   * The numeric value to display.
   */
  number: number | string;

  /**
   * Optional CSS styles to apply to the component.
   */
  sx?: CSSProperties;
  /**
   * Optional options to customize the number formatter.
   * @property {string} currency - The currency to use for formatting. Defaults to the user's preferred currency.
   * @param minimumFractionDigits - The minimum number of fraction digits to display. Defaults to 0.
   * @param maximumFractionDigits - The maximum number of fraction digits to display. Defaults to 2.
   */
  options?: Omit<
    IntlNumberFormatterOptions,
    'liveUpdate' | 'onChange' | 'value'
  >;
}) => {
  const formatter = useNumberFormatter({
    options,
  });

  const res = formatter.format?.(`${number}`);

  return <span style={{ ...sx }}>{res?.formatted ?? ''}</span>;
};

export default StaticNumericSpan;
